import { lazy, Suspense, useEffect, useState } from "react";
import { useScrollEnd } from "@/hooks/useScrollEnd";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Eye, RotateCcw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const createEventLoader = () => import("@/pages/CreateEvent");
const CreateEvent = lazy(createEventLoader);

let preloaded = false;
export const preloadCreateEventModal = () => {
  if (preloaded) return;
  preloaded = true;
  createEventLoader().catch(() => { preloaded = false; });
};

export const CREATE_EVENT_MODAL_OPEN_EVENT = "openCreateEventModal";
export const CREATE_EVENT_MODAL_CLOSE_EVENT = "closeCreateEventModal";

export const openCreateEventModal = () => {
  window.dispatchEvent(new CustomEvent(CREATE_EVENT_MODAL_OPEN_EVENT));
};

export const closeCreateEventModal = () => {
  window.dispatchEvent(new CustomEvent(CREATE_EVENT_MODAL_CLOSE_EVENT));
};

// Free-floating glass tile used by the toolbar buttons above the form
const floatTileClass =
  "h-9 gap-2 rounded-xl border-0 bg-card/70 backdrop-blur-xl px-3 text-xs font-medium shadow-[0_10px_15px_-3px_rgba(0,0,0,0.45)] transition-all duration-300 hover:bg-muted";

const InlineLoader = () => (
  <div className="h-full rounded-2xl bg-card/70 backdrop-blur-xl p-6 space-y-4">
    <Skeleton className="h-7 w-1/2" />
    <Skeleton className="h-48 w-full rounded-xl" />
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <Skeleton className="h-11 w-full rounded-xl mt-4" />
  </div>
);

interface CreateEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateEventModal = ({ open, onOpenChange }: CreateEventModalProps) => {
  const { atEnd: atFormEnd, attach: attachHost } = useScrollEnd({
    panelSelector: ".no-scrollbar",
    captureScroll: true,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="w-[96vw] max-w-[1180px] top-[4.25rem] bottom-[5.25rem] translate-y-0 rounded-none border-0 bg-transparent p-0 shadow-none overflow-visible sm:bottom-[1.5rem]"
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="mb-4 flex h-12 shrink-0 items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              className={floatTileClass}
              onClick={() => window.dispatchEvent(new CustomEvent("createEventModal:preview"))}
            >
              <Eye className="h-4 w-4" />
              Preview
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(floatTileClass, "text-muted-foreground hover:text-destructive")}
              onClick={() => window.dispatchEvent(new CustomEvent("createEventModal:clear"))}
            >
              <RotateCcw className="h-4 w-4" />
              Clear
            </Button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close create event"
              className={cn(
                floatTileClass,
                "ml-auto inline-flex w-9 items-center justify-center px-0 text-foreground"
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={attachHost} className="relative min-h-0 flex-1">
            <Suspense fallback={<InlineLoader />}>
              {open && <CreateEvent embedMode onClose={() => onOpenChange(false)} />}
            </Suspense>
            {/* Bottom shade: hints there is more form below, lifts off at the end */}
            <div
              aria-hidden
              data-at-end={String(atFormEnd)}
              className={cn(
                "pointer-events-none absolute inset-x-0 bottom-0 h-20 rounded-b-lg bg-[linear-gradient(to_top,hsl(var(--background)/0.9),hsl(var(--background)/0.35)_55%,hsl(var(--background)/0))] transition-opacity duration-300",
                atFormEnd ? "opacity-0" : "opacity-100"
              )}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CreateEventModalMount = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    window.addEventListener(CREATE_EVENT_MODAL_OPEN_EVENT, onOpen);
    window.addEventListener(CREATE_EVENT_MODAL_CLOSE_EVENT, onClose);
    return () => {
      window.removeEventListener(CREATE_EVENT_MODAL_OPEN_EVENT, onOpen);
      window.removeEventListener(CREATE_EVENT_MODAL_CLOSE_EVENT, onClose);
    };
  }, []);

  return <CreateEventModal open={open} onOpenChange={setOpen} />;
};
